import { produce } from 'immer';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useSessionStorage } from '@/hooks/useSessionStorage';
import { SearchFilter } from '@/services/ProductsService';

export interface WithSearchProductsFilters {
  searchFilters: Partial<SearchFilter>;
  hasSearchFilters: boolean;
  handleSearchFilters: <K extends keyof SearchFilter>(
    filterKey: K,
    filterValue: SearchFilter[K] | undefined,
  ) => void;
  handleResetFilters: () => void;
}

interface WithSearchProductsFiltersOptions {
  persistFilters?: boolean;
}

export function withSearchProductsFilters<T>(
  Component: React.ComponentType<T & WithSearchProductsFilters>,
  options?: WithSearchProductsFiltersOptions,
) {
  const { persistFilters = false } = options || {};

  return function ComponentWithSearchStates(props: T) {
    const [searchParams, setSearchParams] = useSearchParams();

    // Usa sessionStorage ou useState dependendo da opção de persistir ou não
    const [searchFiltersSession, setSearchFiltersSession] = useSessionStorage<
      Partial<SearchFilter>
    >('searchFilters', {}, ['productId', 'productName', 'productSku']);

    const [searchFiltersState, setSearchFiltersState] = useState<
      Partial<SearchFilter>
    >({});

    const searchFilters = persistFilters
      ? searchFiltersSession
      : searchFiltersState;
    const setSearchFilters = persistFilters
      ? setSearchFiltersSession
      : setSearchFiltersState;

    const hasSearchFilters = searchParams.size > 0;

    const handleSearchFilters = useCallback(
      <K extends keyof SearchFilter>(
        filterKey: K,
        filterValue: SearchFilter[K] | undefined,
      ) => {
        if (typeof filterValue !== 'undefined') {
          searchParams.set(filterKey, String(filterValue));
        } else {
          searchParams.delete(filterKey);
        }

        setSearchParams(searchParams);

        setSearchFilters(
          produce((draft) => {
            draft[filterKey] = filterValue || undefined;
          }),
        );
      },
      [searchParams, setSearchParams, setSearchFilters],
    );

    const handleResetFilters = useCallback(() => {
      setSearchFilters({});
      setSearchParams({}, { replace: true });
    }, [setSearchParams, setSearchFilters]);

    useEffect(() => {
      searchParams.forEach((value, key) => {
        setSearchFilters(
          produce((draft: Record<string, unknown>) => {
            let parsed: unknown;

            if (value === 'true') {
              parsed = true;
            } else {
              parsed = value || undefined;
            }

            draft[key] = parsed;
          }),
        );
      });
    }, [searchParams, setSearchFilters]);

    return (
      <Component
        {...props}
        searchFilters={searchFilters}
        hasSearchFilters={hasSearchFilters}
        handleSearchFilters={handleSearchFilters}
        handleResetFilters={handleResetFilters}
      />
    );
  };
}
